import { AddressType } from "api/types/generated-types";
import { ShipmentAccessorialType } from "app/pages/shipments/types/domain";
import i18next from "i18next";
import { z } from "zod";

const addressSchema = z.object({
  id: z.number().optional(),
  name: z.string().nullable().optional(),
  address_1: z.string().nullable().optional(),
  address_2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
  country_code: z.string().nullable().optional(),
  country_name: z.string().nullable().optional(),
  province_code: z.string().nullable().optional(),
  province_name: z.string().nullable().optional(),
  placeId: z.string().nullable().optional(),
  googlePlaceDescription: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
});

const locationStopSchema = z.object({
  address: addressSchema.refine((val) => val != null, {
    message: i18next.t("quoteRequest:LocationStep.addressRequired"),
  }),
  start: z.string().optional().nullable(),
  end: z.string().optional().nullable(),
  isTimeTBD: z.boolean().optional(),
  accessorials: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        type: z.nativeEnum(ShipmentAccessorialType),
      })
    )
    .optional(),
  note: z.string().nullable().optional(),
});

export const locationSchema = z.object({
  pickup: locationStopSchema,
  delivery: locationStopSchema,
  accessorials: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        type: z.nativeEnum(ShipmentAccessorialType),
      })
    )
    .optional(),
});
// .superRefine((data, ctx) => {
//   const pickupDate = data.pickup.date;
//   const pickupTime = data.pickup?.start;
//   const deliveryDate = data.delivery.date;
//   const deliveryTime = data.delivery?.start;
//   if (pickupDate && deliveryDate) {
//     let pickupDT = pickupDate;
//     let deliveryDT = deliveryDate;
//     if (pickupTime && deliveryTime) {
//       pickupDT = pickupDate.set({ hour: pickupTime.hour, minute: pickupTime.minute });
//       deliveryDT = deliveryDate.set({ hour: deliveryTime.hour, minute: deliveryTime.minute });
//     }
//     if (pickupDT > deliveryDT) {
//       ctx.addIssue({
//         code: z.ZodIssueCode.custom,
//         message: i18next.t("quoteRequest:LocationStep.crossFieldError"),
//         path: ["pickup.date"],
//       });
//     }
//   }
// });

export type LocationSchema = z.infer<typeof locationSchema>;

function mapToAddressType(type: string | null | undefined): AddressType | undefined {
  if (!type) return undefined;
  if (Object.values(AddressType).includes(type as AddressType)) {
    return type as AddressType;
  }
  return undefined;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const transformAddress = (address: z.infer<typeof addressSchema>) => ({
  id: address?.id,
  name: address?.name,
  address1: address?.address_1,
  address2: address?.address_2,
  city: address?.city,
  zip: address?.zip,
  countryCode: address?.country_code,
  countryName: address?.country_name,
  provinceCode: address?.province_code,
  provinceName: address?.province_name,
  googlePlaceId: address?.placeId,
  googlePlaceDescription: address?.googlePlaceDescription,
  type: mapToAddressType(address?.type),
});

export const shipmentSchema = locationSchema.and(
  z.object({
    reference_number: z.string().max(64, "Reference number is too long").optional(),
    internal_reference_number: z.string().optional().nullable(),
    hide_internal_ref_till_dispatch: z.boolean().optional(),
    deadline_respond_at: z.string({
      required_error: "Urgency is required",
    }),
    is_hazardous: z.boolean().optional(),
    hazardous_goods_details: z.string().max(256, "Details too long").optional(),
    goods_value: z.preprocess(
      (val) => (val === "" || val === undefined ? undefined : Number(val)),
      z
        .number({
          invalid_type_error: "Goods value must be a number",
          required_error: "Goods value is required",
        })
        .positive("Goods value must be a positive number")
        .optional()
    ),
    trailer_type: z.string({
      invalid_type_error: "Equipment type must be a string",
      required_error: "Equipment type is required",
    }),
    min_temp: z
      .preprocess(
        (val) => (val === "" ? undefined : Number(val)),
        z.number({
          invalid_type_error: "Minimum temperature must be a number",
        })
      )
      .optional(),
    max_temp: z
      .preprocess(
        (val) => (val === "" ? undefined : Number(val)),
        z.number({
          invalid_type_error: "Maximum temperature must be a number",
        })
      )
      .optional(),
    note: z.string().max(2048, "Notes too long").nullable().optional(),
    ltl_load_spec: z
      .object({
        package_groups: z
          .array(
            z.object({
              packaging_type: z.string({
                invalid_type_error: "Item type is required",
                required_error: "Item type is required",
              }),
              is_stackable: z.boolean().optional(),
              item_quantity: z.preprocess(
                (val) => (val === "" ? undefined : Number(val)),
                z
                  .number({
                    invalid_type_error: "Item quantity must be a number",
                    required_error: "Item quantity is required",
                  })
                  .positive("Item quantity must be a positive number")
              ),
              weight_type: z.string().optional(),
              weight_per_package: z.preprocess(
                (val) => (val === "" ? undefined : Number(val)),
                z
                  .number({
                    invalid_type_error: "Weight must be a number",
                    required_error: "Weight is required",
                  })
                  .positive("Weight must be a number")
              ),
              length_per_package: z.preprocess(
                (val) => (val === "" ? undefined : Number(val)),
                z
                  .number({
                    invalid_type_error: "Length must be a number",
                    required_error: "Length is required",
                  })
                  .positive("Length must be a positive number")
              ),
              width_per_package: z.preprocess(
                (val) => (val === "" ? undefined : Number(val)),
                z
                  .number({
                    invalid_type_error: "Width must be a number",
                    required_error: "Width is required",
                  })
                  .positive("Width must be a positive number")
              ),
              height_per_package: z.preprocess(
                (val) => (val === "" ? undefined : Number(val)),
                z
                  .number({
                    invalid_type_error: "Height must be a number",
                    required_error: "Height is required",
                  })
                  .positive("Height must be a positive number")
              ),
              freight_class: z.string().optional().nullable(),
              nmfc_code: z.string().optional().nullable(),
              commodities: z.string().optional().nullable(),
            })
          )
          .min(1, "At least one package group is required"),
      })
      .optional(),
  })
);

export type ShipmentSchema = z.infer<typeof shipmentSchema>;

export const partnersSchema = shipmentSchema.and(
  z.object({
    partners: z.object({ contact_id: z.number() }).array().min(1, { message: "At least one partner must be selected" }),
  })
);

export type PartnersScheam = z.infer<typeof partnersSchema>;

export const teamSchema = partnersSchema.and(
  z.object({
    teammates: z.object({ contact_id: z.number() }).array().optional(),
  })
);

export type TeamSchema = z.infer<typeof teamSchema>;

export type FinalSchema = TeamSchema;
